import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Plus,
  Pause,
  Loader2,
  Trash2,
  Edit,
  BarChart3,
  History,
  RefreshCw
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LEAD_TYPE_COLORS = {
  cold_call: { bg: 'bg-blue-100', text: 'text-blue-800', badge: 'COLD' },
  live_transfer: { bg: 'bg-green-100', text: 'text-green-800', badge: 'LIVE' },
  data_lead: { bg: 'bg-purple-100', text: 'text-purple-800', badge: 'DATA' },
  robots: { bg: 'bg-cyan-100', text: 'text-cyan-800', badge: 'ROBO' },
  business_leads: { bg: 'bg-amber-100', text: 'text-amber-800', badge: 'BIZ' }
};

const BID_TYPE_COLORS = {
  shared: { bg: 'bg-orange-100', text: 'text-orange-800' },
  exclusive: { bg: 'bg-teal-100', text: 'text-teal-800' }
};

export default function ClientCampaignsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState([]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('client_token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchCampaigns = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/client-portal/campaigns`, { headers: getAuthHeaders() });
      setCampaigns(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/client-portal');
      } else {
        toast.error('Failed to load campaigns');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    const token = localStorage.getItem('client_token');
    if (!token) {
      navigate('/client-portal');
      return;
    }
    fetchCampaigns();
  }, [navigate, fetchCampaigns]);

  const toggleCampaign = async (campaignId, currentStatus) => {
    try {
      await axios.post(
        `${API_URL}/api/client-portal/campaigns/${campaignId}/toggle`,
        { is_active: !currentStatus },
        { headers: getAuthHeaders() }
      );
      setCampaigns(campaigns.map(c => 
        c.id === campaignId ? { ...c, is_active: !currentStatus } : c
      ));
      toast.success(`Campaign ${!currentStatus ? 'activated' : 'paused'}`);
    } catch (error) {
      toast.error('Failed to toggle campaign');
    }
  };

  const pauseAllCampaigns = async () => {
    try {
      const response = await axios.post(
        `${API_URL}/api/client-portal/campaigns/pause-all`,
        {},
        { headers: getAuthHeaders() }
      );
      fetchCampaigns();
      toast.success(`Paused ${response.data.paused_count} campaigns`);
    } catch (error) {
      toast.error('Failed to pause campaigns');
    }
  };

  const deleteCampaign = async (campaignId) => {
    if (!window.confirm('Are you sure you want to delete this campaign?')) return;
    
    try {
      await axios.delete(
        `${API_URL}/api/client-portal/campaigns/${campaignId}`,
        { headers: getAuthHeaders() }
      );
      toast.success('Campaign deleted');
      fetchCampaigns();
    } catch (error) {
      toast.error('Failed to delete campaign');
    }
  };

  const getLeadTypeStyle = (leadType) => {
    return LEAD_TYPE_COLORS[leadType] || { bg: 'bg-gray-100', text: 'text-gray-800', badge: leadType };
  };

  const getBidTypeStyle = (bidType) => {
    return BID_TYPE_COLORS[bidType] || { bg: 'bg-gray-100', text: 'text-gray-800' };
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const activeCampaignsCount = campaigns.filter(c => c.is_active).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="client-campaigns-page">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Campaigns</h1>
            <p className="text-muted-foreground">Manage your lead campaigns and targeting</p>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={fetchCampaigns}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button
              variant="outline"
              onClick={pauseAllCampaigns}
              disabled={activeCampaignsCount === 0}
              className="text-red-600 hover:text-red-700"
            >
              <Pause className="w-4 h-4 mr-2" />
              Pause All Campaigns
            </Button>
            <Button onClick={() => navigate('/client-portal/campaigns/create')} className="bg-teal-600 hover:bg-teal-700">
              <Plus className="w-4 h-4 mr-2" />
              Create New Campaign
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8 space-y-6">
        {/* Campaign Stats */}
        <div className="grid grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="text-sm text-muted-foreground">Total Campaigns</div>
              <div className="text-2xl font-bold">{campaigns.length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-sm text-muted-foreground">Active</div>
              <div className="text-2xl font-bold text-green-600">{activeCampaignsCount}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-sm text-muted-foreground">Total Leads</div>
              <div className="text-2xl font-bold">{campaigns.reduce((sum, c) => sum + (c.stats?.leads || 0), 0)}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-sm text-muted-foreground">Total Spend</div>
              <div className="text-2xl font-bold">{formatCurrency(campaigns.reduce((sum, c) => sum + (c.stats?.spend || 0), 0))}</div>
            </CardContent>
          </Card>
        </div>

        {/* Campaigns Table */}
        <Card>
          <CardHeader className="bg-slate-800 text-white rounded-t-lg">
            <div className="grid grid-cols-7 gap-4 w-full text-sm font-medium">
              <div>Status</div>
              <div>Vertical</div>
              <div className="col-span-2">Campaign Info</div>
              <div>Bid</div>
              <div>Leads / Spend / CPL</div>
              <div>Tools</div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {campaigns.length === 0 ? (
              <div className="p-12 text-center text-muted-foreground">
                <BarChart3 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p className="mb-4">No campaigns yet. Create your first campaign to start receiving leads.</p>
                <Button onClick={() => navigate('/client-portal/campaigns/create')} className="bg-teal-600 hover:bg-teal-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Your First Campaign
                </Button>
              </div>
            ) : (
              <div className="divide-y">
                {campaigns.map((campaign) => {
                  const leadStyle = getLeadTypeStyle(campaign.lead_type);
                  const bidStyle = getBidTypeStyle(campaign.bid_type);
                  return (
                    <div key={campaign.id} className="grid grid-cols-7 gap-4 p-4 items-center hover:bg-muted/50">
                      {/* Status Toggle */}
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => toggleCampaign(campaign.id, campaign.is_active)}
                          className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                            campaign.is_active 
                              ? 'bg-green-100 text-green-800 hover:bg-green-200' 
                              : 'bg-red-100 text-red-800 hover:bg-red-200'
                          }`}
                        >
                          {campaign.is_active ? 'ON' : 'OFF'}
                        </button>
                      </div>
                      
                      {/* Vertical Icon */}
                      <div>
                        <div className="w-10 h-10 rounded-full bg-red-500 flex items-center justify-center text-white">
                          <Plus className="w-5 h-5" />
                        </div>
                      </div>
                      
                      {/* Campaign Info */}
                      <div className="col-span-2">
                        <div 
                          className="font-medium text-teal-600 hover:underline cursor-pointer" 
                          onClick={() => navigate(`/client-portal/campaigns/${campaign.id}/edit`)}
                        >
                          {campaign.name}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge className={`${leadStyle.bg} ${leadStyle.text} text-xs`}>
                            {leadStyle.badge}
                          </Badge>
                          <Badge className={`${bidStyle.bg} ${bidStyle.text} text-xs`}>
                            {campaign.bid_type?.toUpperCase()}
                          </Badge>
                          {campaign.states?.length < 51 && (
                            <Badge variant="outline" className="text-xs">
                              {campaign.states?.length} states
                            </Badge>
                          )}
                        </div>
                      </div>
                      
                      {/* Bid */}
                      <div>
                        <div className="font-medium">{formatCurrency(campaign.max_bid)}</div>
                        <div className="text-xs text-muted-foreground">Max Bid</div>
                      </div>
                      
                      {/* Stats */}
                      <div className="text-sm">
                        <div>{campaign.stats?.leads || 0} leads</div>
                        <div>{formatCurrency(campaign.stats?.spend || 0)}</div>
                        <div className="text-muted-foreground">CPL: {formatCurrency(campaign.stats?.cpl || 0)}</div>
                      </div>
                      
                      {/* Tools */}
                      <div className="flex items-center gap-1">
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={() => navigate(`/client-portal/campaigns/${campaign.id}/history`)} 
                          title="View History"
                        >
                          <History className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={() => navigate(`/client-portal/campaigns/${campaign.id}/edit`)} 
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={() => navigate(`/client-portal/reports?campaign=${campaign.id}`)} 
                          title="Reports"
                        >
                          <BarChart3 className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={() => deleteCampaign(campaign.id)} 
                          title="Delete" 
                          className="text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Totals Row */}
        {campaigns.length > 0 && (
          <div className="flex justify-end gap-8 text-sm font-medium px-4">
            <div>TOTALS:</div>
            <div>{campaigns.reduce((sum, c) => sum + (c.stats?.leads || 0), 0)} leads</div>
            <div>{formatCurrency(campaigns.reduce((sum, c) => sum + (c.stats?.spend || 0), 0))}</div>
            <div>
              {formatCurrency(
                campaigns.reduce((sum, c) => sum + (c.stats?.spend || 0), 0) / 
                Math.max(1, campaigns.reduce((sum, c) => sum + (c.stats?.leads || 0), 0))
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
